import {
  APP_ROUTES,
  FilteredSpecialistOptions,
  LegacyFilteredSpecialistOptions,
  LOCAL_STORAGE_KEYS,
  PaymentMethodOptions,
  ServiceTypeOptions,
} from "@src/constants";
import {
  useApplicationContext,
  useFeatureExposure,
  useFeatureFlags,
  useFeatureManifest,
} from "@src/context";
import type { Address } from "@src/context/ApplicationContext";
import { useEntryRouteBootstrap } from "@src/hooks/useEntryRouteBootstrap";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useReasonsForVisit } from "@src/hooks/useReasonsForVisit";
import { useInsurancePackages } from "@src/routes/GetStarted/hooks/useInsurancePackages";
import { fetchAddressSearch } from "@src/services/address-search.service";
import ClientLogsService from "@src/services/client-logs.service";
import { getTimezoneData } from "@src/services/get-timezone.service";
import {
  isEapInsurancePolicy,
  shouldRouteInsurancePolicyToContact,
} from "@src/utils/role-guardrails";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Text } from "@uhg-abyss/web/ui/Text";
import { TextInput } from "@uhg-abyss/web/ui/TextInput";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FindProvider } from "./FindProvider";

const ModalContent = styled("div", {
  width: "100%",
  display: "flex",
  flexDirection: "column",
  gap: "$core.spacing.400",
  maxWidth: "420px",
  margin: "0 auto",
});

const ModalActions = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  gap: "$core.spacing.300",
  marginTop: "$core.spacing.200",
});

const isAddress = (value: unknown): value is Address => {
  const address = value as Partial<Address> | undefined;
  return Boolean(
    address?.city &&
      address?.state &&
      address?.state_code &&
      address.position &&
      typeof address.context_id === "number",
  );
};

/**
 * Production treatment shell. It deliberately collects only a trusted UUID
 * (bootstrapped above this route) and a ZIP-resolved location before search.
 */
export const CombinedFindProvider = () => {
  const { data, setDataPatch } = useApplicationContext();
  const navigate = useNavigateWithUTM();
  const { initialEntryPath } = useFeatureManifest();
  const { FEATURE_UPDATED_REASON_FOR_VISIT, FEATURE_REASONS_FOR_VISIT_API } = useFeatureFlags();
  const { reasonsForVisit, error: reasonsForVisitError } = useReasonsForVisit();
  const [zip, setZip] = useState("");
  const [reasonForVisit, setReasonForVisit] = useState<string[]>(() => data.specialities ?? []);
  const [typeOfService, setTypeOfService] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [resolvedLocation, setResolvedLocation] = useState<{
    zip: string;
    address: Address;
  } | null>(null);
  const [error, setError] = useState("");
  const [isIntakeDismissed, setIsIntakeDismissed] = useState(false);
  const [isInitialSearchPending, setIsInitialSearchPending] = useState(false);
  const hasLoggedDirectEntryRef = useRef(false);
  const shouldShowIntake = !data.address && !isIntakeDismissed;
  const isDirectFindProviderEntry = initialEntryPath === APP_ROUTES.FIND_PROVIDER;
  const completeInitialSearch = useCallback(() => setIsInitialSearchPending(false), []);

  useEntryRouteBootstrap({
    initReady: isDirectFindProviderEntry,
    isReferrerFlow: false,
    // FeatureManifestProvider must establish the identity before it can assign
    // this treatment. Reuse that UUID while completing the entry attribution.
    canEnsureUuid: false,
    digitalInfoLogLocation: "CombinedFindProviderDirectEntry",
  });

  useEffect(() => {
    if (!isDirectFindProviderEntry || !data.userUuid || hasLoggedDirectEntryRef.current) return;
    hasLoggedDirectEntryRef.current = true;
    ClientLogsService.logWebAction(data.userUuid, "CombinedFindProvider", "find_provider_direct", {
      acquisitionEntryRoute: "find_provider_direct",
    });
  }, [data.userUuid, isDirectFindProviderEntry]);
  const { insurancePolicies } = useInsurancePackages(
    data.userUuid,
    resolvedLocation ? String(resolvedLocation.address.context_id) : undefined,
    resolvedLocation?.address.state_code,
    paymentMethod,
  );
  const insurancePolicyOptions = useMemo(
    () =>
      insurancePolicies.map((policy) => ({
        value: String(policy.id),
        label: policy.name,
      })),
    [insurancePolicies],
  );
  const reasonForVisitOptions = useMemo(() => {
    const legacyOptions = FEATURE_UPDATED_REASON_FOR_VISIT
      ? FilteredSpecialistOptions
      : LegacyFilteredSpecialistOptions;
    const options = FEATURE_REASONS_FOR_VISIT_API
      ? reasonsForVisit.map((reason) => reason.display)
      : legacyOptions;
    return options.map((value) => ({ label: value, value }));
  }, [FEATURE_REASONS_FOR_VISIT_API, FEATURE_UPDATED_REASON_FOR_VISIT, reasonsForVisit]);

  useFeatureExposure(
    "COMBINED_FIND_PROVIDER",
    "combined_find_provider_treatment",
    APP_ROUTES.FIND_PROVIDER,
    shouldShowIntake && Boolean(data.userUuid),
  );

  // Match the POC's insurance experience by resolving a complete ZIP while the
  // modal remains open. This lets package options load without making the Find
  // providers action wait; submit still reuses the production address/timezone
  // validation path below.
  useEffect(() => {
    const normalizedZip = zip.trim();
    setResolvedLocation(null);
    setSelectedPolicy("");
    if (!shouldShowIntake || !/^\d{5}$/.test(normalizedZip)) return;

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      const result = await fetchAddressSearch(
        normalizedZip,
        data.userUuid,
        data.userRole,
        data.testRecord,
      );
      if (!cancelled && isAddress(result)) {
        setDataPatch({ userRecordCreated: true });
        setResolvedLocation({ zip: normalizedZip, address: result });
      }
    }, 300);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [data.testRecord, data.userRole, data.userUuid, setDataPatch, shouldShowIntake, zip]);

  const submit = () => {
    const normalizedZip = zip.trim();
    if (!/^\d{5}$/.test(normalizedZip)) {
      setError("Enter a valid 5-digit ZIP code.");
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "CombinedFindProvider",
        "zip_invalid",
      );
      return;
    }

    setError("");
    const matchedInsurancePolicy =
      paymentMethod === "insurance"
        ? insurancePolicies.find((policy) => String(policy.id) === selectedPolicy)
        : undefined;

    if (shouldRouteInsurancePolicyToContact(matchedInsurancePolicy, data.userRole)) {
      navigate(APP_ROUTES.CONTACT, {
        replace: false,
        state: {
          exclusionNote: matchedInsurancePolicy?.exclusion_note,
          isEap: isEapInsurancePolicy(matchedInsurancePolicy),
        },
      });
      return;
    }

    // Dismiss immediately into the POC skeleton surface. Address and timezone
    // prerequisites resolve in the background; ProviderList remains blocked by
    // the missing address, so it cannot issue a partial search.
    setIsIntakeDismissed(true);
    setIsInitialSearchPending(true);

    void (async () => {
      const result =
        resolvedLocation?.zip === normalizedZip
          ? resolvedLocation.address
          : await fetchAddressSearch(normalizedZip, data.userUuid, data.userRole, data.testRecord);
      if (!isAddress(result)) {
        setIsInitialSearchPending(false);
        setIsIntakeDismissed(false);
        setError(
          (result as { error?: string } | undefined)?.error || "We could not find that ZIP code.",
        );
        ClientLogsService.logWebAction(
          data.userUuid || "unknown",
          "CombinedFindProvider",
          "zip_lookup_failed",
        );
        return;
      }
      setDataPatch({ userRecordCreated: true });

      // The normal Get Started flow resolves the IANA timezone from the ZIP's
      // coordinates before availability is requested. The combined treatment
      // bypasses that screen, so it must perform the same prerequisite here.
      const timezoneResult = await getTimezoneData(
        result.position.lat,
        result.position.lon,
        data.userUuid || "unknown",
      );
      if (!timezoneResult?.success || !timezoneResult.data) {
        setIsInitialSearchPending(false);
        setIsIntakeDismissed(false);
        setError("We could not determine the timezone for that ZIP code.");
        ClientLogsService.logWebAction(
          data.userUuid || "unknown",
          "CombinedFindProvider",
          "timezone_lookup_failed",
        );
        return;
      }

      const insurancePolicy = matchedInsurancePolicy ?? null;

      setDataPatch({
        address: result,
        zip: normalizedZip,
        userTimezone: timezoneResult.data,
        // Keep clinician and care-format filters unset. The original providers-first flow
        // supplied this established age default for availability requests; without
        // it, the existing ProviderList intentionally does not load availability.
        providerType: undefined,
        typeOfService: typeOfService || undefined,
        careFormat: undefined,
        ageRange: "Adult (30-64)",
        sessionType: "no preference",
        paymentMethod: paymentMethod || undefined,
        insurancePolicies,
        insurancePolicy,
        selectedPolicy: insurancePolicy ? String(insurancePolicy.id) : null,
        specialities: reasonForVisit,
        providerList: undefined,
        isCombinedFindProviderTreatment: true,
      });
      ClientLogsService.logWebAction(
        data.userUuid || "unknown",
        "CombinedFindProvider",
        "provider_search_submitted",
        {
          locationAcquisition: "zip_entered",
          reasonForVisit: reasonForVisit.length > 0 ? "provided" : "skipped",
          typeOfService: typeOfService ? "provided" : "skipped",
          paymentMethod: paymentMethod ? "provided" : "skipped",
        },
      );
    })();
  };

  return (
    <>
      <FindProvider
        key={`combined-find-provider-${data.address?.context_id ?? "intake"}`}
        isBrowseIntakePending={!data.address}
        isCombinedInitialSearchPending={isInitialSearchPending}
        onCombinedInitialSearchComplete={completeInitialSearch}
      />
      {shouldShowIntake && (
        <ModalDialog
          isOpen
          size={480}
          hideClose
          closeOnClickOutside={false}
          aria-label="Schedule an appointment today"
          data-testid="find-provider-combined-zip-modal"
        >
          <ModalContent
            data-testid="find-provider-combined-treatment"
            css={{ gap: "$core.spacing.200" }}
          >
            <Text css={{ fontSize: "24px", fontWeight: 700 }}>Schedule an appointment today</Text>
            <Text>These details will help us match you with providers</Text>
            <TextInput
              id="obc-find-provider-combined-zip-input"
              data-testid="find-provider-combined-zip-input"
              data-obc-label="Combined find provider ZIP code"
              label="Patient zip code"
              autoComplete="postal-code"
              value={zip}
              onChange={(event) => setZip(event.target.value)}
              errorMessage={error}
              isRequired
            />
            <SelectInput
              id="obc-find-provider-combined-reason-for-visit"
              data-testid="find-provider-combined-reason-for-visit"
              data-obc-label="Combined find provider reason for visit"
              label="Reason for visit"
              aria-label="Select reason for visit"
              value={reasonForVisit}
              onChange={(selectedOptions) => {
                const values = Array.isArray(selectedOptions)
                  ? selectedOptions.map(String)
                  : selectedOptions
                    ? [String(selectedOptions)]
                    : [];
                setReasonForVisit(values);
              }}
              options={reasonForVisitOptions}
              isClearable
              isSearchable
              isMulti
              errorMessage={reasonsForVisitError}
            />
            <SelectInput
              id="obc-find-provider-combined-type-of-service"
              data-testid="find-provider-combined-type-of-service"
              data-obc-label="Combined find provider type of service"
              label="Type of service desired"
              value={typeOfService}
              onChange={(value) => setTypeOfService(String(value || ""))}
              options={ServiceTypeOptions.map((value) => ({ label: value, value }))}
            />
            <SelectInput
              id="obc-find-provider-combined-payment-method"
              data-testid="find-provider-combined-payment-method"
              data-obc-label="Combined find provider payment method"
              label="How does the patient plan to pay?"
              value={paymentMethod}
              onChange={(value) => {
                setSelectedPolicy("");
                setPaymentMethod(String(value || ""));
              }}
              options={PaymentMethodOptions}
            />
            {paymentMethod === "insurance" && (
              <SelectInput
                id="obc-find-provider-combined-insurance-policy"
                data-testid="find-provider-combined-insurance-policy"
                data-obc-label="Combined find provider insurance policy"
                label="What is the patient's insurance policy?"
                value={selectedPolicy}
                onChange={(value) => setSelectedPolicy(String(value || ""))}
                options={insurancePolicyOptions}
                isDisabled={insurancePolicyOptions.length === 0}
                isSearchable
              />
            )}
            <ModalActions>
              <Button
                id="obc-find-provider-combined-submit"
                data-testid="find-provider-combined-submit"
                data-obc-label="Find providers"
                onClick={submit}
              >
                Find providers
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalDialog>
      )}
    </>
  );
};
